import { type FeatureAttributes } from './generated/FeatureAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import { model } from '../modelBuilder';
import { AtRiskFlaggable } from './concerns/AtRiskFlaggable';
import { HasCustomFieldValues } from './concerns/HasCustomFieldValues';
import { HasEstimates } from './concerns/HasEstimates';
import { HasExtensionFields } from './concerns/HasExtensionFields';
import { HasRecordPositions } from './concerns/HasRecordPositions';
import { TeamAssignable } from './concerns/TeamAssignable';

interface Feature
  extends FeatureAttributes,
    AtRiskFlaggable,
    HasEstimates,
    HasExtensionFields,
    HasCustomFieldValues,
    HasRecordPositions,
    TeamAssignable {}

@model
@AtRiskFlaggable
@HasEstimates
@HasExtensionFields
@HasCustomFieldValues
@HasRecordPositions
@TeamAssignable
class Feature extends ApplicationModel {
  // Start auto-generated
  static typename: 'Feature' = 'Feature';
  get typename(): 'Feature' {
    return 'Feature';
  }

  static internalTypename: 'Feature' = 'Feature';
  get internalTypename(): 'Feature' {
    return 'Feature';
  }

  static terminologyName = 'Feature';

  static inputType = 'FeatureAttributes!';

  static filterType = 'FeatureFilters!';
  static filters = {
    active: 'Boolean',
    iterationId: 'ID',
    id: '[ID!]',
    projectId: 'ID',
    teamId: 'ID',
    releaseId: 'ID',
    programIncrementId: 'ID',
    assignedToUserId: 'ID',
    hasBookmarkPosition: 'HasBookmarkPositionFilter',
    workflowMeaning: '[WorkflowMeaningEnum!]',
  };

  static fields = {
    commentsCount: attr({ readable: true }),
    createdAt: attr({ readable: true }),
    currentSchedulingTeamsCount: attr({ readable: true }),
    dependenciesCount: attr({ readable: true }),
    drillInUrl: attr({
      args: {
        publishableId: 'ID',
        publishableType: 'String',
        viewSnapshotId: 'ID',
        dashboardId: 'ID',
        exportType: 'String',
        bookmarkId: 'ID',
      },
      readable: true,
    }),
    dueDate: attr({ readable: true }),
    durationInTeamWorkflowStatus: attr({ readable: true }),
    epicId: attr({ readable: true }),
    goalsCount: attr({ readable: true }),
    id: attr({ readable: true }),
    initiativeId: attr({ readable: true }),
    integrationLinksCount: attr({ readable: true }),
    iterationId: attr({ readable: true }),
    manualRiskComment: attr({ writable: true, readable: true }),
    name: attr({ writable: true, readable: true }),
    path: attr({ readable: true }),
    position: attr({ writable: true, readable: true }),
    prioritizedBacklogRank: attr({ readable: true }),
    programIncrementId: attr({ readable: true }),
    progress: attr({ readable: true }),
    projectId: attr({ readable: true }),
    referenceNum: attr({ readable: true }),
    releaseId: attr({ readable: true }),
    releasePhaseId: attr({ readable: true }),
    requirementsCount: attr({ readable: true }),
    riskFactors: attr({ readable: true, json: true }),
    riskFactorsCount: attr({ readable: true }),
    schedulingTeamsCount: attr({ readable: true }),
    score: attr({ readable: true }),
    scoringSystemId: attr({ readable: true }),
    showFeatureRemainingEstimate: attr({ readable: true }),
    startDate: attr({ readable: true }),
    suggestedEstimateMinutes: attr({
      args: { useCache: 'Boolean' },
      readable: true,
    }),
    tagList: attr({ writable: true, readable: true }),
    tagsCount: attr({ readable: true }),
    teamId: attr({ readable: true }),
    teamLocation: attr({ readable: true }),
    teamWorkflowStatusId: attr({ readable: true }),
    updatedAt: attr({ readable: true }),
    watchersCount: attr({ readable: true }),
    workflowKindId: attr({ readable: true }),
    workflowStatusId: attr({ readable: true }),
    assignedToUser: attr.belongsTo({
      writeShape: ['id', 'name', 'email'],
      readable: true,
      typename: 'User',
    }),
    bestEstimate: attr.belongsTo({ readable: true, typename: 'Estimate' }),
    createdByUser: attr.belongsTo({ readable: true, typename: 'User' }),
    description: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Note',
    }),
    epic: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Epic',
    }),
    initialEstimate: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    initiative: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Initiative',
    }),
    iteration: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Iteration',
    }),
    originalEstimate: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    permissions: attr.belongsTo({
      readable: true,
      typename: 'ResourcePermissions',
    }),
    programIncrement: attr.belongsTo({
      readable: true,
      typename: 'ProgramIncrement',
    }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    release: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Release',
    }),
    releasePhase: attr.belongsTo({ readable: true, typename: 'ReleasePhase' }),
    remainingEstimate: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    researchFolder: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Page',
    }),
    screenDefinition: attr.belongsTo({
      readable: true,
      typename: 'ScreenDefinition',
    }),
    team: attr.belongsTo({
      writeShape: ['id', 'name', 'referencePrefix'],
      readable: true,
      typename: 'Project',
    }),
    teamWorkflowStatus: attr.belongsTo({
      writeShape: ['id', 'name', 'category'],
      readable: true,
      typename: 'WorkflowStatus',
    }),
    terminology: attr.belongsTo({
      readable: true,
      typename: 'TerminologyItem',
    }),
    workDone: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    workflowKind: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'WorkflowKind',
    }),
    workflowStatus: attr.belongsTo({
      writeShape: ['id', 'name', 'category'],
      readable: true,
      typename: 'WorkflowStatus',
    }),
    addGoals: attr.hasMany({ writable: true, readable: false }),
    bookmarksRecordPositions: attr.hasMany({
      args: { filters: 'RecordPositionFilters' },
      readable: true,
    }),
    childRecordLinks: attr.hasMany({
      args: { filters: 'RecordLinkFilters' },
      readable: true,
    }),
    comments: attr.hasMany({ readable: true }),
    currentSchedulingTeams: attr.hasMany({ readable: true }),
    customFieldValues: attr.hasMany({
      args: { ids: '[ID!]', keys: '[String!]' },
      readable: true,
    }),
    extensionFields: attr.hasMany({
      args: { filters: 'ExtensionFieldFilters' },
      readable: true,
    }),
    goals: attr.hasMany({ writable: true, readable: true }),
    integrationLinks: attr.hasMany({ readable: true }),
    parentRecordLinks: attr.hasMany({
      args: { filters: 'RecordLinkFilters' },
      readable: true,
    }),
    removeGoals: attr.hasMany({ writable: true, readable: false }),
    requirements: attr.hasMany({ readable: true }),
    schedulingTeams: attr.hasMany({ readable: true }),
    scoreFacts: attr.hasMany({ writable: true, readable: true }),
    tags: attr.hasMany({ readable: true }),
    tasks: attr.hasMany({ readable: true }),
    watchers: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  /** @returns {Aha.Epic} */
  get container() {
    return this.epic;
  }

  get roadmapPosition() {
    const release = this.release;
    const parkingLot = release?.parkingLot || false;

    // Provide a stable sort, prioritizing by release date first, then feature position within release
    return [
      parkingLot ? null : release.releaseDate,
      parkingLot ? release.position : null,
      release?.id,
      this.position,
      this.id,
    ];
  }
}

export default Feature;
